import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import TopicCard from "@/components/base/TopicCard";
import Reveal from "@/components/base/Reveal";
import useSeo from "@/hooks/useSeo";
import { categories } from "@/mocks/categories";
import { topics } from "@/mocks/topics";
import { formatHeat } from "@/utils/format";
import type { Topic } from "@/types/content";

type SortKey = "hot" | "new" | "comment";

const sortOptions: { key: SortKey; label: string; icon: string }[] = [
  { key: "hot", label: "热度最高", icon: "ri-fire-line" },
  { key: "new", label: "最新发布", icon: "ri-time-line" },
  { key: "comment", label: "讨论最多", icon: "ri-chat-3-line" },
];

export default function TopicsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get("cat") || "all";
  const [sortKey, setSortKey] = useState<SortKey>("hot");
  const [keyword, setKeyword] = useState(searchParams.get("q") || "");

  useSeo({
    title: "话题广场 - 深夜吃瓜 · 全网热话题分类讨论区",
    description:
      "深夜吃瓜话题广场汇集娱乐圈、影视剧综、综艺现场、网红直播、音乐现场、体育电竞、数码科技等分类讨论，支持按热度与最新排序，快速找到值得参与的评论区。",
    keywords: "话题广场,深夜吃瓜,话题分类,讨论区,评论区,观点",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "深夜吃瓜话题广场",
        description: "按分类浏览全网热话题讨论区，支持热度与最新排序。",
        inLanguage: "zh-CN",
        url: "https://ag11k.cn/topics",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://ag11k.cn/" },
          { "@type": "ListItem", position: 2, name: "话题广场", item: "https://ag11k.cn/topics" },
        ],
      },
    ],
  });

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    let list: Topic[] = topics.filter((topic) => {
      const matchCat = activeCat === "all" || topic.category === activeCat;
      const matchKw =
        !kw ||
        topic.title.toLowerCase().includes(kw) ||
        topic.summary.toLowerCase().includes(kw) ||
        topic.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCat && matchKw;
    });

    if (sortKey === "hot") {
      list = [...list].sort((a, b) => b.heat - a.heat);
    } else if (sortKey === "comment") {
      list = [...list].sort((a, b) => b.comments - a.comments);
    }

    return list;
  }, [activeCat, keyword, sortKey]);

  const handleCat = (catId: string) => {
    const next = new URLSearchParams(searchParams);
    if (catId === "all") {
      next.delete("cat");
    } else {
      next.set("cat", catId);
    }
    setSearchParams(next, { replace: true });
  };

  const activeCategory = categories.find((c) => c.id === activeCat);

  return (
    <SiteLayout>
      <section className="border-b border-background-200/60 bg-background-100/60">
        <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
          <Breadcrumb items={[{ label: "话题广场" }]} />
          <h1 className="heading-serif mt-5 text-3xl font-black text-foreground-950 md:text-4xl">
            {activeCategory ? `${activeCategory.name} · 讨论区` : "话题广场"}
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-500 md:text-base">
            {activeCategory
              ? activeCategory.desc
              : "所有正在被讨论的瓜都在这里。按分类筛选、按热度或最新排序，也可以直接搜索你关心的关键词。"}
          </p>
        </div>
      </section>

      <section className="bg-background-50 py-8 md:py-12">
        <div className="mx-auto max-w-content px-4 md:px-6">
          <div className="sticky top-16 z-30 -mx-4 mb-8 border-b border-background-200/60 bg-background-50/95 px-4 py-3 backdrop-blur md:top-[68px] md:mx-0 md:rounded-lg md:border md:border-background-200/70 md:px-5">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="no-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
                <button
                  type="button"
                  onClick={() => handleCat("all")}
                  className={`flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm transition-colors ${
                    activeCat === "all"
                      ? "border-primary-500 bg-primary-500 text-background-50 dark:text-foreground-950"
                      : "border-background-300/70 text-foreground-600 hover:border-primary-500/60 hover:text-primary-500"
                  }`}
                >
                  <i className="ri-apps-2-line" />
                  全部
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => handleCat(cat.id)}
                    className={`flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm transition-colors ${
                      activeCat === cat.id
                        ? "border-primary-500 bg-primary-500 text-background-50 dark:text-foreground-950"
                        : "border-background-300/70 text-foreground-600 hover:border-primary-500/60 hover:text-primary-500"
                    }`}
                  >
                    <i className={cat.icon} />
                    {cat.name}
                  </button>
                ))}
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative">
                  <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-500" />
                  <input
                    type="search"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="搜索话题或关键词"
                    aria-label="搜索话题"
                    className="w-full rounded-md border border-background-300/70 bg-background-100 py-2 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none sm:w-56"
                  />
                </div>
                <div className="flex items-center gap-1 rounded-full bg-background-200/70 p-1">
                  {sortOptions.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setSortKey(opt.key)}
                      className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                        sortKey === opt.key
                          ? "bg-background-50 text-foreground-950"
                          : "text-foreground-600 hover:text-foreground-950"
                      }`}
                    >
                      <i className={opt.icon} />
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mb-6 flex items-center justify-between text-sm text-foreground-500">
            <span>
              共找到 <strong className="text-primary-500">{filtered.length}</strong> 个话题
              {keyword.trim() && ` · 关键词「${keyword.trim()}」`}
            </span>
            <span className="hidden items-center gap-1.5 sm:flex">
              <i className="ri-pulse-line text-secondary-500" />
              热度实时更新中
            </span>
          </div>

          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((topic, index) => (
                <Reveal key={topic.id} delay={(index % 3) * 50}>
                  <TopicCard topic={topic} />
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300/70 bg-background-100/50 px-6 py-20 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-emotion-sad-line text-3xl" />
              </span>
              <h2 className="heading-serif mt-5 text-lg font-bold text-foreground-950">
                暂时没有匹配的话题
              </h2>
              <p className="mt-2 max-w-sm text-sm text-foreground-500">
                试试换个关键词，或者切换到「全部」分类，看看广场上还有哪些热闹的讨论。
              </p>
              <button
                type="button"
                onClick={() => {
                  setKeyword("");
                  handleCat("all");
                }}
                className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
              >
                <i className="ri-refresh-line" />
                重置筛选条件
              </button>
            </div>
          )}

          {filtered.length > 0 && (
            <p className="mt-10 text-center text-xs text-foreground-500">
              已展示全部 {filtered.length} 个话题 · 当前最高热度 {formatHeat(filtered[0].heat)}
            </p>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}